import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import modsFactoryApi from '../../api/modsFactoryApi';
import modsfactoryUrls from "../../configs/mods_factory/modsfactoryUrls";
import { AddonInterface } from '../../store/mods_factory/addonsSlice';
import { UserInterface } from "../../store/mods_factory/usersSlice";
import { userEntity, usersEntity } from "./usersActions";

const PREFIX = 'mods_factory';

const processStrategy = (value: any) => {
  return {
    ...value,
    url: modsfactoryUrls.addonPath(value.id),
    image_url: modsfactoryUrls.remoteUrl(value.image_url),
    texture_url: modsfactoryUrls.remoteUrl(value.texture_url),
    zip_url: modsfactoryUrls.remoteUrl(value.zip_url),
    ico_url: modsfactoryUrls.remoteUrl(value.ico_url)
  };
}

export const addonsEntity = new schema.Entity(
  'addons',
  {
    user: usersEntity
  },
  {
    processStrategy
  }
);

export const addonEntity = new schema.Entity(
  'addon',
  {
    user: userEntity
  },
  {
    processStrategy
  }
);

const fetchAddonsCollection = async (url: string, params: object = {}) => {
  const response    = await modsFactoryApi.get(url, { params })
  const normalized  = normalize<
    any,
    {
      users: { [key: string]: UserInterface };
      addons: { [key: string]: AddonInterface };
    }
  >(response.data.addons, [addonsEntity]);

  return {
    users: normalized.entities.users || {},
    addons: normalized.entities?.addons || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      user: { [key: string]: UserInterface };
      addon: AddonInterface;
    }
  >(response.data.addon, addonEntity);
  return normalized.entities;
}

export const fetchPreviewAddons = createAsyncThunk(
  `${PREFIX}_addons/fetchPreviewAddons`,
  async (params: object = {}) => fetchAddonsCollection('/onesky/addons', params)
);

export const fetchAddons = createAsyncThunk(
  `${PREFIX}_addons/fetchAddons`,
  async (params: object = {}) => fetchAddonsCollection('/onesky/addons', params)
);

export const fetchAddonsTrending = createAsyncThunk(
  `${PREFIX}_addons/fetchAddons`,
  async (params: object = {}) => fetchAddonsCollection('/onesky/addons/trending', params)
);

export const fetchAddonsPopular = createAsyncThunk(
  `${PREFIX}_addons/fetchAddons`,
  async (params: object = {}) => fetchAddonsCollection('/onesky/addons/popular', params)
);

export const fetchAddonsRecents = createAsyncThunk(
  `${PREFIX}_addons/fetchAddons`,
  async (params: object = {}) => fetchAddonsCollection('/onesky/addons/recents', params)
);

export const fetchAddon = createAsyncThunk(
  `${PREFIX}_addons/fetchAddon`,
  async (id: number | string) => {
    const response = await modsFactoryApi.get(`/onesky/addons/${id}`);
    return normalizeInstance(response);
  }
);

export const updateAddon = createAsyncThunk(
  `${PREFIX}_addons/updateAddon`,
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await modsFactoryApi.patch(`/onesky/addons/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroyAddon = createAsyncThunk(
  `${PREFIX}_addons/destroyAddon`,
  async (id: number) => {
    const response = await modsFactoryApi.delete(`/onesky/addons/${id}`);
    return response.data;
  }
);
